import {useSearchParams} from 'react-router-dom';
import {
    Typography,
    Grid,
    CardContent,
    LinearProgress,
} from '@mui/material';
import MainCard from '../components/MainCard';
import PaginationCard from '../components/PaginationCard';
import ErrorCard from '../components/ErrorCard';
import QueryProcessor from '../components/QueryProcessor';
import TxList from '../components/TxList';
import {useSummary, useTxs, useAddress} from '../queries';
import React from "react";

const allowedTxTypes = ['any', 'coinbase', 'transfer'];
const allowedPageSizes = [10, 25, 50, 100];

/* eslint-disable-next-line */
export interface TxViewProps {
}

export function TxView(props: TxViewProps) {
    const [searchParams] = useSearchParams();

    const blockHeight = Number(searchParams.get('block') ?? -1);
    const address = searchParams.get('address') ?? '';
    if (!isNaN(blockHeight) && blockHeight >= 0) {
        return <ViewBlockTxs blockHeight={blockHeight}/>;
    } else if (address.length > 0) {
        return <ViewBlockAddress address={address}/>;
    } else {
        return <ViewHistTxs/>;
    }
}

interface ViewBlockTxsProps {
    blockHeight: number;
}

function ViewBlockTxs({blockHeight}: ViewBlockTxsProps) {
    const txsQuery = useTxs(blockHeight, '', 'any', undefined, 100);
    return (
        <QueryProcessor
            query={txsQuery}
            dataValidator={(data: any) => data.type === 'txs'}
            renderer={(data: any) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography component={'span'} variant={'h2'}>
                            Transactions{' '}
                        </Typography>
                        <Typography component={'span'} variant={'h5'} color={'textSecondary'}>
                            in block #{blockHeight}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <MainCard>
                            <CardContent>
                                <TxList data={data.payload}/>
                            </CardContent>
                        </MainCard>
                    </Grid>
                </Grid>
            )}
        />
    );
}

interface ViewBlockAddressProps {
    address: string;
}

function ViewBlockAddress({address}: ViewBlockAddressProps) {
    const [searchParams] = useSearchParams();
    let txType = searchParams.get('type') ?? 'any';
    if (!allowedTxTypes.includes(txType)) {
        txType = 'any';
    }

    let pageSize = Number(searchParams.get('ps') ?? 25);
    if (isNaN(pageSize) || !allowedPageSizes.includes(pageSize)) {
        pageSize = 25;
    }

    let pageNo = Number(searchParams.get('p') ?? 1);
    if (isNaN(pageNo) || pageNo < 1) {
        pageNo = 1;
    }

    const addressQuery = useAddress(address);

    return (
        <QueryProcessor
            query={addressQuery}
            dataValidator={(data: any) => data.type === 'address'}
            renderer={(data: any) => {
                const d = data.payload;
                let totalTxs = 0;
                if (txType === 'coinbase') {
                    totalTxs = d.totalCoinbaseTx;
                } else if (txType === 'transfer') {
                    totalTxs = d.totalTransferTx;
                } else {
                    totalTxs = d.totalCoinbaseTx + d.totalTransferTx;
                }
                return <ViewTxs address={address} txType={txType} totalTxs={totalTxs} pageSize={pageSize}
                                pageNo={pageNo}/>;
            }}
        />
    );
}

/* eslint-disable-next-line */
interface ViewHistTxsProps {
}

function ViewHistTxs(props: ViewHistTxsProps) {
    const [searchParams] = useSearchParams();
    let txType = searchParams.get('type') ?? 'any';
    if (!allowedTxTypes.includes(txType)) {
        txType = 'any';
    }

    let pageSize = Number(searchParams.get('ps') ?? 15);
    if (isNaN(pageSize) || !allowedPageSizes.includes(pageSize)) {
        pageSize = 15;
    }

    let pageNo = Number(searchParams.get('p') ?? 1);
    if (isNaN(pageNo) || pageNo < 1) {
        pageNo = 1;
    }

    const summaryQuery = useSummary();

    return (
        <QueryProcessor
            query={summaryQuery}
            dataValidator={(data: any) => data.type === 'summary'}
            renderer={(data: any) => {
                const d = data.payload;
                let totalTxs = 0;
                if (txType === 'coinbase') {
                    totalTxs = d.latestBlock.height + 1;
                } else if (txType === 'transfer') {
                    totalTxs = d.totalTxs - (d.latestBlockHeight + 1);
                } else {
                    totalTxs = d.totalTxs;
                }
                return <ViewTxs address="" txType={txType} totalTxs={totalTxs} pageSize={pageSize} pageNo={pageNo}/>;
            }}
        />
    );
}

interface ViewTxsProps {
    address: string;
    txType: string;
    totalTxs: number;
    pageSize: number;
    pageNo: number;
}

function ViewTxs({address, txType, totalTxs, pageSize, pageNo}: ViewTxsProps) {
    const txSeqnoBegin = (pageNo - 1) * pageSize;
    const txSeqnoEnd = txSeqnoBegin + pageSize;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography component={'span'} variant={'h2'}>
                    Transactions{' '}
                </Typography>
                {
                    <Typography component={'span'} variant={'h5'} color={'textSecondary'}>
                        (type = {txType}, total = {totalTxs.toLocaleString()}
                        {address.length > 0 ? `, address = ${address}${address.length === 70 ? '...****' : ''}` : ''})
                    </Typography>
                }
            </Grid>
            <Grid item xs={12}>
                <PaginationCard totalItems={totalTxs} pageSize={pageSize} pageNo={pageNo}>
                    <ViewTxsContent
                        address={address}
                        txType={txType}
                        totalTxs={totalTxs}
                        txSeqnoBegin={txSeqnoBegin}
                        txSeqnoEnd={txSeqnoEnd}
                    />
                </PaginationCard>
            </Grid>
        </Grid>
    );
}

interface ViewTxsContentProps {
    address: string;
    txType: string;
    totalTxs: number;
    txSeqnoBegin: number;
    txSeqnoEnd: number;
}

function ViewTxsContent({address, txType, totalTxs, txSeqnoBegin, txSeqnoEnd}: ViewTxsContentProps) {
    const fetchCount = 100;
    const startForTxSeqnoBegin = Math.floor(txSeqnoBegin / fetchCount) * fetchCount;
    const startForTxSeqnoEnd = Math.floor(txSeqnoEnd / fetchCount) * fetchCount;

    const qs = [
        useTxs(-1, address, txType, startForTxSeqnoBegin, fetchCount, {
            enabled: startForTxSeqnoBegin !== startForTxSeqnoEnd,
        }),
        useTxs(-1, address, txType, startForTxSeqnoEnd, fetchCount),
    ];

    if (qs.some((q) => q.isLoading) || qs.some((q) => q.isRefetching)) {
        return <LinearProgress color={'warning'}/>;
    }

    if (qs.some((q) => q.isError) || qs.some((q) => !q.isSuccess)) {
        return (
            <ErrorCard enableGoBack={true}>
                <Typography variant={'h4'}>Failed fetching transactions data.</Typography>
            </ErrorCard>
        );
    }

    const data =
        startForTxSeqnoBegin === startForTxSeqnoEnd ? qs[0].data.payload : [...qs[0].data.payload, ...qs[1].data.payload];

    const dataBegin = txSeqnoBegin - startForTxSeqnoBegin;
    const dataEnd = dataBegin + (txSeqnoEnd - txSeqnoBegin);
    return <TxList data={data.filter((d: any, idx: number) => dataBegin <= idx && idx < dataEnd)}/>;
}

export default TxView;
